import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import useConversation from '../../../hooks/assistant/use-conversation';
import useRestartConversation from '../../../hooks/assistant/use-restart-conversation';
import useSendMessage from '../../../hooks/assistant/use-send-message';
import AssistantHeader from './assistant-header';
import ConversationBar from './conversation-bar';
import MessageInput from './message-input';
import MessageList from './message-list';
import statusKey from './status-key';
import toBubbles from './to-bubbles';

type AssistantChatProps =
  /** A window has its own header, with a pin to dock it at the side and a close button. */
  | {
      variant: 'window';
      isPinned: boolean;
      onTogglePin: () => void;
      onClose: () => void;
    }
  /** A page has its own header, with a way back. */
  | { variant: 'page'; onBack: () => void };

/** The conversation itself: header, messages and the box to ask in. Where it sits is up to the caller. */
export default function AssistantChat(props: AssistantChatProps) {
  const { t } = useTranslation();
  const { title, messages } = useConversation();
  const { sendMessage, isSending, pendingContent, answerText, status } =
    useSendMessage();
  const { restart } = useRestartConversation();
  const [question, setQuestion] = useState('');

  const bubbles = toBubbles({
    messages,
    pendingContent,
    answerText,
    errorText: t('ASSISTANT_ERROR'),
  });

  const send = async () => {
    const content = question.trim();
    if (!content || isSending) {
      return;
    }

    setQuestion('');
    await sendMessage(content);
  };

  const restartConversation = async () => {
    await restart();
  };

  return (
    <>
      {props.variant === 'window' && (
        <AssistantHeader
          title={title}
          canClear={bubbles.length > 0}
          isPinned={props.isPinned}
          onTogglePin={props.onTogglePin}
          onClear={restartConversation}
          onClose={props.onClose}
        />
      )}
      {props.variant === 'page' && (
        <ConversationBar
          title={title}
          messageCount={bubbles.length}
          onBack={props.onBack}
          onClear={restartConversation}
        />
      )}
      <MessageList
        bubbles={bubbles}
        isWaiting={isSending && !answerText}
        status={t(statusKey(status))}
      />
      <MessageInput
        value={question}
        onChange={setQuestion}
        onSend={send}
        isSending={isSending}
        autoFocus={props.variant === 'window'}
      />
    </>
  );
}
